import { useState } from "react";
import { Check, ChevronsUpDown, MapPin, X, Clock, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const POPULAR = ["Jakarta", "Bandung", "Semarang", "Yogyakarta", "Surabaya", "Solo", "Bekasi", "Depok", "Bogor", "Tangerang"];
const RECENT_KEY = "mp_recent_cities";

const getRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
};
const pushRecent = (name) => {
  try {
    const list = [name, ...getRecent().filter((n) => n !== name)].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch { /* ignore */ }
};

export const CityCombobox = ({ cities = [], value, onChange, placeholder = "Pilih kota", testId }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState(getRecent);

  const select = (name) => {
    onChange(name);
    pushRecent(name);
    setRecent(getRecent());
    setQuery("");
    setOpen(false);
  };

  const validNames = new Set(cities.map((c) => c.name));
  const recentCities = recent.filter((n) => validNames.has(n)).slice(0, 5);
  const popularCities = POPULAR.filter((n) => validNames.has(n) && !recentCities.includes(n));
  const showQuickPicks = query.trim() === "";

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setRecent(getRecent()); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="h-11 w-full justify-between px-3 font-normal"
        >
          <span className="flex min-w-0 items-center gap-2">
            <MapPin className="h-4 w-4 shrink-0 text-primary" />
            <span className={cn("truncate", !value && "text-muted-foreground")}>
              {value || placeholder}
            </span>
          </span>
          {value ? (
            <X
              className="h-4 w-4 shrink-0 opacity-60 hover:opacity-100"
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              data-testid={testId ? `${testId}-clear` : undefined}
            />
          ) : (
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(val, search) => (val.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
        >
          <CommandInput
            placeholder="Ketik nama kota..."
            value={query}
            onValueChange={setQuery}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <CommandList>
            <CommandEmpty>Kota tidak ditemukan.</CommandEmpty>

            {showQuickPicks && recentCities.length > 0 && (
              <CommandGroup heading={<span className="flex items-center gap-1.5"><Clock className="h-3 w-3" /> Terakhir Dipilih</span>}>
                <div className="flex flex-wrap gap-1.5 px-1 py-1.5" data-testid={testId ? `${testId}-recent` : undefined}>
                  {recentCities.map((n) => (
                    <button
                      key={`recent-${n}`}
                      type="button"
                      data-testid={testId ? `${testId}-recent-${n}` : undefined}
                      onClick={() => select(n)}
                      className="rounded-full border border-border bg-secondary px-3 py-1 text-xs text-foreground transition-colors hover:border-primary/60 hover:text-primary"
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </CommandGroup>
            )}

            {showQuickPicks && popularCities.length > 0 && (
              <CommandGroup heading={<span className="flex items-center gap-1.5"><Star className="h-3 w-3" /> Kota Populer</span>}>
                <div className="flex flex-wrap gap-1.5 px-1 py-1.5" data-testid={testId ? `${testId}-popular` : undefined}>
                  {popularCities.map((n) => (
                    <button
                      key={`pop-${n}`}
                      type="button"
                      data-testid={testId ? `${testId}-popular-${n}` : undefined}
                      onClick={() => select(n)}
                      className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/60 hover:text-primary"
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </CommandGroup>
            )}

            <CommandGroup heading={showQuickPicks ? "Semua Kota" : undefined}>
              {cities.map((c) => (
                <CommandItem
                  key={c.slug || c.name}
                  value={`${c.name} ${c.province || ""}`}
                  data-testid={testId ? `${testId}-option-${(c.slug || c.name)}` : undefined}
                  onSelect={() => select(c.name)}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === c.name ? "opacity-100" : "opacity-0")} />
                  <span className="flex-1">{c.name}</span>
                  {c.province && <span className="ml-2 text-xs text-muted-foreground">{c.province}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
